import axios from '../config/axiosConfig';

const API_URL = '/atomic';

// Get all atomic tests with pagination and filtering
const getAtomicTests = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.techniqueId) params.append('techniqueId', filters.techniqueId);
  if (filters.platform) params.append('platform', filters.platform);
  if (filters.executorType) params.append('executorType', filters.executorType);
  if (filters.search) params.append('search', filters.search);
  if (filters.tactic) params.append('tactic', filters.tactic);
  if (filters.page) params.append('page', filters.page);
  if (filters.limit) params.append('limit', filters.limit);
  if (filters.sort) params.append('sort', filters.sort);

  const response = await axios.get(`${API_URL}?${params.toString()}`);
  return response.data;
};

// Get atomic tests by technique ID
const getAtomicTestsByTechnique = async (techniqueId) => {
  const response = await axios.get(`${API_URL}/technique/${techniqueId}`);
  return response.data;
};

// Get single atomic test by ID
const getAtomicTest = async (id) => {
  const response = await axios.get(`${API_URL}/by-id/${id}`);
  return response.data;
};

// Get atomic tests for a project
const getAtomicTestsByProject = async (projectId) => {
  const response = await axios.get(`${API_URL}/project/${projectId}`);
  return response.data;
};

// Search atomic tests
const searchAtomicTests = async (query) => {
  const response = await axios.get(`${API_URL}/search/${encodeURIComponent(query)}`);
  return response.data;
};

// Get library statistics summary
const getStatsSummary = async () => {
  const response = await axios.get(`${API_URL}/stats/summary`);
  return response.data;
};

// Get available platforms
const getPlatforms = async () => {
  const response = await axios.get(`${API_URL}/stats/platforms`);
  return response.data;
};

// Get available executor types
const getExecutorTypes = async () => {
  const response = await axios.get(`${API_URL}/stats/executors`);
  return response.data;
};

// Get coverage statistics for a project
const getCoverage = async (projectId) => {
  const response = await axios.get(`${API_URL}/stats/coverage/${projectId}`);
  return response.data;
};

// Get available techniques in library
const getAvailableTechniques = async () => {
  const response = await axios.get(`${API_URL}/available-techniques`);
  return response.data;
};

// ===========================================
// EXECUTION ENDPOINTS
// ===========================================

// Execute atomic test on a single agent
const executeTest = async (testId, agentId, options = {}) => {
  const response = await axios.post(`${API_URL}/execute`, {
    testId,
    agentId,
    inputValues: options.inputValues || {},
    targetIds: options.targetIds || [],
    project: options.project,
    createAction: options.createAction !== false,
    timeout: options.timeout || 300
  });
  return response.data;
};

// Execute atomic test on multiple agents
const executeTestBulk = async (testId, agentIds, options = {}) => {
  const response = await axios.post(`${API_URL}/execute-bulk`, {
    testId,
    agentIds,
    filters: options.filters,
    inputValues: options.inputValues || {},
    targetIds: options.targetIds || [],
    project: options.project,
    createActions: options.createActions !== false,
    timeout: options.timeout || 300
  });
  return response.data;
};

// Execute multiple atomic tests as a chain
const executeTestChain = async (testIds, agentId, options = {}) => {
  const response = await axios.post(`${API_URL}/execute-chain`, {
    testIds,
    agentId,
    inputValues: options.inputValues || {},
    targetIds: options.targetIds || [],
    project: options.project,
    delayBetweenTests: options.delayBetweenTests || 5,
    stopOnError: options.stopOnError || false
  });
  return response.data;
};

// ===========================================
// IMPORT/SYNC ENDPOINTS
// ===========================================

// Import atomic tests from JSON data
const importTests = async (tests, replace = false) => {
  const response = await axios.post(`${API_URL}/import`, {
    tests,
    replace
  });
  return response.data;
};

// Sync a specific technique from GitHub
const syncTechnique = async (techniqueId) => {
  const response = await axios.post(`${API_URL}/sync-technique/${techniqueId}`);
  return response.data;
};

// Delete all atomic tests
const deleteAllTests = async () => {
  const response = await axios.delete(`${API_URL}/all`);
  return response.data;
};

// ===========================================
// PURPLEHQ CUSTOM TESTS
// ===========================================

// Get all tests (Atomic Red Team + PurpleHQ custom tests)
const getAllPurpleHQTests = async () => {
  const response = await axios.get(`${API_URL}/purplehq/all`);
  return response.data;
};

// Get only custom PurpleHQ tests
const getCustomTests = async () => {
  const response = await axios.get(`${API_URL}/purplehq/custom`);
  return response.data;
};

// Create new custom PurpleHQ test
const createCustomTest = async (testData) => {
  const response = await axios.post(`${API_URL}/purplehq/custom`, testData);
  return response.data;
};

// Update existing custom test
const updateCustomTest = async (id, testData) => {
  const response = await axios.put(`${API_URL}/purplehq/custom/${id}`, testData);
  return response.data;
};

// Delete custom test
const deleteCustomTest = async (id) => {
  const response = await axios.delete(`${API_URL}/purplehq/custom/${id}`);
  return response.data;
};

// Clone official test as custom (allows modification)
const cloneTest = async (id) => {
  const response = await axios.post(`${API_URL}/purplehq/clone/${id}`);
  return response.data;
};

// ===========================================
// HELPER FUNCTIONS
// ===========================================

// Get platform badge color
const getPlatformColor = (platform) => {
  const colors = {
    windows: 'bg-blue-500',
    linux: 'bg-orange-500',
    darwin: 'bg-gray-500',
    macos: 'bg-gray-500',
    'iaas:aws': 'bg-yellow-500',
    'iaas:azure': 'bg-blue-400',
    'iaas:gcp': 'bg-red-400',
    containers: 'bg-cyan-500',
    'office-365': 'bg-orange-400',
    'azure-ad': 'bg-blue-300',
    'google-workspace': 'bg-green-500'
  };
  return colors[platform] || 'bg-gray-400';
};

// Get executor type icon
const getExecutorIcon = (executorType) => {
  const icons = {
    powershell: '⚡',
    cmd: '💻',
    command_prompt: '💻',  // Alias for cmd
    bash: '🐚',
    sh: '🐚',
    shell: '🖥️',
    python: '🐍',
    manual: '📋'
  };
  return icons[executorType] || '🔧';
};

// Get tactic display name
const getTacticDisplayName = (tactic) => {
  const names = {
    'reconnaissance': 'Reconnaissance',
    'resource-development': 'Resource Development',
    'initial-access': 'Initial Access',
    'execution': 'Execution',
    'persistence': 'Persistence',
    'privilege-escalation': 'Privilege Escalation',
    'defense-evasion': 'Defense Evasion',
    'credential-access': 'Credential Access',
    'discovery': 'Discovery',
    'lateral-movement': 'Lateral Movement',
    'collection': 'Collection',
    'command-and-control': 'Command and Control',
    'exfiltration': 'Exfiltration',
    'impact': 'Impact'
  };
  return names[tactic] || tactic;
};

// Substitute variables in command
const substituteVariables = (command, inputArgs, providedValues = {}) => {
  if (!command) return command;
  let result = command;

  if (inputArgs && Array.isArray(inputArgs)) {
    inputArgs.forEach(arg => {
      const value = providedValues[arg.name] !== undefined ? providedValues[arg.name] : arg.default;
      if (value !== undefined) {
        const pattern = new RegExp(`#\\{${arg.name}\\}`, 'g');
        result = result.replace(pattern, String(value));
      }
    });
  }

  return result;
};

const atomicService = {
  // Query
  getAtomicTests,
  getAtomicTestsByTechnique,
  getAtomicTest,
  getAtomicTestsByProject,
  searchAtomicTests,
  getAvailableTechniques,

  // Stats
  getStatsSummary,
  getPlatforms,
  getExecutorTypes,
  getCoverage,

  // Execution
  executeTest,
  executeTestBulk,
  executeTestChain,

  // Import/Sync
  importTests,
  syncTechnique,
  deleteAllTests,

  // PurpleHQ Custom Tests
  getAllPurpleHQTests,
  getCustomTests,
  createCustomTest,
  updateCustomTest,
  deleteCustomTest,
  cloneTest,

  // Helpers
  getPlatformColor,
  getExecutorIcon,
  getTacticDisplayName,
  substituteVariables
};

export default atomicService;
